<script setup lang="ts">
type TitleVariant = 'x-large' | 'large' | 'medium' | 'small-semibold' | 'small' | 'x-small' | 'xx-small';

const props = withDefaults(
  defineProps<{
    text?: string
    tag?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | (string & {})
    variant?: TitleVariant
    balanced?: boolean
  }>(),
  {
    variant: `medium`,
    balanced: true,
  },
);

defineSlots<{
  default: () => unknown
}>();

const VARIANT_TO_TAG: Record<TitleVariant, 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'> = {
  'x-large': `h1`,
  'large': 'h2',
  'medium': 'h3',
  'small-semibold': 'h4',
  'small': 'h4',
  'x-small': 'h5',
  'xx-small': 'h6',
} as const;

const tagWithDefault = computed(() => {
  if (typeof props.tag === 'string') return props.tag;
  return VARIANT_TO_TAG[props.variant];
});

// font-title
// Rule is: >= 20px (text-xl) go to `font-title`
const fontSizeAndFamilyClasses: Record<TitleVariant, string> = {
  'x-large': `font-title text-4xl lg:text-7xl`,
  'large': `font-title text-3xl/7 lg:text-[2rem]`,
  'medium': `font-title text-2xl lg:text-3xl`,
  'small-semibold': `font-title text-xl lg:text-2xl`,
  'small': `font-title text-xl lg:text-2xl`,
  'x-small': `text-lg`,
  'xx-small': `text-base`,
};
const activeClasses: Record<TitleVariant, string> = {
  'x-large': `font-semibold group-[.is-prestige]:font-normal`,
  'large': `font-semibold group-[.is-prestige]:font-light`,
  'medium': `font-semibold group-[.is-prestige]:font-light`,
  'small-semibold': `font-semibold group-[.is-prestige]:font-light`,
  'small': ``,
  'x-small': `font-semibold group-[.is-prestige]:font-light`,
  'xx-small': `font-semibold group-[.is-prestige]:font-normal`,
} as const;

const classes = computed(() => {
  return [
    props.balanced ? `text-balance` : `text-wrap`,
    'group-[.is-prestige]:uppercase',
    fontSizeAndFamilyClasses[props.variant],
    activeClasses[props.variant],
  ];
});
</script>

<template>
  <component
    :is="tagWithDefault"
    :class="classes"
  >
    <slot>
      {{ text }}
    </slot>
  </component>
</template>
